// Granular per-user permissions that an admin can grant at user creation / edit time.
// These decide EXACTLY what a non-admin user can see/use. Admin always has everything.
// A user only sees the features that are granted to them (via these permissions or
// their role's minimal core defaults below) — nothing else.
import { COMPANY_SITES } from './companySites';

export const PERMISSIONS = [
  { key: 'dashboard', label: 'Financial Dashboard (revenue overview)' },
  { key: 'song_library', label: 'Song Library (view songs)' },
  { key: 'audio_upload', label: 'Audio Upload (Song Library me gaane upload / status) — Distribution Staff default' },
  { key: 'thumbnail_upload', label: 'Thumbnail Upload (thumbnails + song posters) — Graphic Designer default' },
  { key: 'youtube_channels', label: 'YouTube Channels (list only — name/subs, NO revenue)' },
  { key: 'channel_revenue', label: 'YouTube Channel Revenue (₹/$) — show earnings' },
  { key: 'income', label: 'Income (revenue)' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'excel_upload', label: 'Excel Upload' },
  { key: 'song_agreements', label: 'Song Agreements / Music Distribution' },
  { key: 'agreement_generator', label: 'Agreement Generator (auto Singer/Studio NOC Word files)' },
  { key: 'reports_view', label: 'View Reports' },
  { key: 'video_edit_log', label: 'Video Edit Log' },
  { key: 'thumbnails', label: 'Thumbnail Library (view)' },
  { key: 'thumbnail_download', label: 'Thumbnail Download (single / weekly / bulk)' },
  { key: 'audio_download', label: 'Audio / Song Download' },
  { key: 'video_download', label: 'Video Downloader (YouTube → full quality / 4K)' },
  { key: 'salary_view', label: 'My Salary / Attendance / Performance (apna hisab dekhe)' },
  { key: 'staff_management', label: 'Staff Management (profiles, attendance, salary)' },
  { key: 'staff_tasks', label: 'Staff Tasks (assign & manage tasks)' },
  { key: 'ai_audio_upload', label: 'AI Audio Upload (upload AI-generated audio)' },
  { key: 'ai_audio_download', label: 'AI Audio Download (listen & download AI audio)' },
  { key: 'song_metadata', label: 'Song Metadata (view/edit/download song details)' },
  { key: 'claim_links', label: 'Claim Links (YouTube claim links per company)' },
  { key: 'video_tracker', label: 'Video Tracker (song upload tracker + mark uploaded)' },
  { key: 'store_links', label: 'Store Links (ISRC/UPC se Spotify, Apple, JioSaavn ke links)' },
  { key: 'trending', label: 'YouTube Trending (category-wise trending videos, tags, hashtags)' },
  { key: 'ai_assistant', label: 'AI Assistant (AI se apne data ke baare me poochhe)' },
  { key: 'ai_tools', label: 'AI Tools (YouTube SEO writer + email writer)' },
  // Company ki apni websites — jisko tick karoge usi ko wo site dikhegi.
  ...COMPANY_SITES.map(s => ({ key: s.key, label: `Company Site: ${s.name} (${s.url.replace('https://', '')})` })),
];

// Minimal CORE defaults per role. These are the role's fundamental function and are
// always available for that role. Everything else must be explicitly granted by admin,
// so granting/removing extra access in the Users page directly controls what shows.
// (Admin is not listed — admin always has everything.)
export const ROLE_DEFAULT_PERMS = {
  manager: ['dashboard', 'song_library', 'youtube_channels', 'channel_revenue', 'income', 'expenses', 'excel_upload', 'song_agreements', 'reports_view', 'thumbnails', 'thumbnail_download', 'audio_download', 'video_edit_log'],
  hr_manager: ['staff_management', 'staff_tasks', 'salary_view', 'song_library', 'excel_upload', 'song_agreements', 'reports_view', 'thumbnails', 'thumbnail_download', 'audio_download', 'video_download', 'video_edit_log'],
  viewer: ['dashboard', 'song_library', 'youtube_channels', 'channel_revenue', 'income', 'expenses', 'excel_upload', 'song_agreements', 'reports_view', 'thumbnails'],
  graphic_designer: ['thumbnails', 'thumbnail_download', 'thumbnail_upload'],
  video_editor: ['video_edit_log'],
  poster_viewer: [],
  distribution_staff: ['audio_upload'],
};

// Compulsory permissions per role — these are always forced on and cannot be unticked.
export const ROLE_COMPULSORY_PERMS = {
  graphic_designer: ['thumbnails', 'thumbnail_download', 'thumbnail_upload'],
  distribution_staff: ['audio_upload'],
  hr_manager: ['staff_management', 'staff_tasks', 'salary_view'],
};

export function getPerms(user) {
  if (!user) return [];
  const p = user.permissions;
  if (Array.isArray(p)) return p;
  try { return JSON.parse(p || '[]'); } catch { return []; }
}

// Admin is always allowed. For every other user, access is EXACTLY what the admin
// has granted (the explicit permissions list) — nothing else. So when an admin
// unticks a feature in "Edit Access", it is truly removed; when ticked, it appears.
// (Role defaults are only used to pre-fill the Edit Access dialog for older users
//  who were never given an explicit list — see effectivePerms below.)
export function hasPerm(user, key) {
  if (!user) return false;
  if (user.role === 'admin' || user.role === 'super_admin') return true;
  const perms = getPerms(user);
  // If user has explicit permissions set by admin, ONLY those count.
  // Role defaults are only a fallback for legacy users with no saved permissions.
  if (perms.length) return perms.includes(key) || (ROLE_COMPULSORY_PERMS[user.role] || []).includes(key);
  return (ROLE_DEFAULT_PERMS[user.role] || []).includes(key);
}

// What an admin should see pre-ticked when editing a user's access. If the user
// already has an explicit list, use it as-is; otherwise fall back to the role's
// core defaults so legacy users aren't shown an empty dialog.
export function effectivePerms(user) {
  if (!user) return [];
  const p = getPerms(user);
  if (p.length) return p;
  return ROLE_DEFAULT_PERMS[user.role] || [];
}
